import { onBeforeUnmount, onMounted, ref, type Ref } from 'vue';

/**
 * Drives `SiteHeader`'s `scrolled` prop for a page with no hero to hand off
 * from.
 *
 * `LandingScreen.vue` derives `scrolled` by watching its own hero logo leave
 * the viewport — the header's mark fades in exactly as the hero's fades out,
 * so the two never appear together. The news pages have no hero and no
 * second logo to stage that handoff against; they mount `SiteHeader` with
 * `scrolled` left at its default `false` forever (see that component's own
 * note), which is the bug this fixes: the bar stays the bare, transparent
 * "top of the landing page" state for the whole scroll, so a news page's own
 * text slides under an unbacked, logo-less strip rather than a real header.
 *
 * Same mechanism as `LandingScreen.vue` otherwise — an `IntersectionObserver`
 * rather than a scroll listener, for the same reason: no per-frame work, and
 * "has this left the screen" is the observer's native question. The sentinel
 * is a dedicated, zero-height element (mounted right after the header)
 * rather than the hero's `<h1>`, because there is no hero here for it to
 * double as.
 *
 * No `rootMargin` shrink, unlike `LandingScreen.vue`'s `-80px`. That value is
 * tuned to a *big* observed block (the hero title) whose top starts well
 * clear of the header and whose bottom stays past the shrunk boundary until a
 * real scroll — this sentinel is a single point sitting right at the header's
 * own bottom edge (~66px down), which a -80px margin puts outside the root
 * before any scrolling happens at all: measured, the header reported
 * `lintel-scrolled` at rest. Left at the default 0px margin, the threshold is
 * exactly "has the content that started just under the header scrolled out
 * from under it" — the right question with nothing else to synchronise
 * against.
 *
 * The sentinel ref is created by the caller, not here — a `ref="x"` template
 * binding is only recognised as a read of `x` when `x` is declared directly
 * in that component's own `<script setup>`, so a ref this function created
 * and returned would type-check as unused wherever it's bound in the caller's
 * template.
 */
export function useScrolledHeader(sentinel: Ref<HTMLElement | null>): { scrolled: Ref<boolean> } {
  // Same degradation as `LandingScreen.vue`: no `IntersectionObserver` means
  // the header renders exactly as its scrolled state always did, rather than
  // stranding it bare with no way out.
  const scrolled = ref(!('IntersectionObserver' in window));
  let observer: IntersectionObserver | null = null;

  onMounted(() => {
    const target = sentinel.value;
    if (!target || !('IntersectionObserver' in window)) return;
    observer = new IntersectionObserver(([entry]) => {
      scrolled.value = !entry.isIntersecting;
    });
    observer.observe(target);
  });

  onBeforeUnmount(() => {
    observer?.disconnect();
    observer = null;
  });

  return { scrolled };
}
